/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 * File Path: packages/sharedlib/src/stores/auth.store.ts
 */

import type { User, UserAuth } from '@repo/common';
import type { OnboardingData } from '@repo/common/types/Onboarding';
import { derived, writable, type Readable } from 'svelte/store';
import constants from '../utils/constants';
import type { StatusEnum } from '../utils/store.util';
import { createStoreHandle } from '../utils/store.util';
const FILENAME = '.user.dat';

const dummyUser: User = {
  createdAt: '',
  deletedAt: '',
  email: '',
  firstName: '',
  id: '',
  lastName: '',
  picture: '',
  notifications: {
    threads_digest: false,
    meeting_prep: false,
    product_updates: false,
    account_emails: false
  },
  profile: {
    displayLanguage: 'en',
    selectedLanguages: ['', ''],
    role: null,
    roles: [],
    currentOnboardingStepIndex: 0
  }
};

const dummyUserAuth: UserAuth = constants.BYPASS_AUTH
  ? {
    idToken: 'mock-id-token',
    refreshToken: 'mock-refresh-token',
    apiToken: 'mock-api-token',
    apiTokenM: 'mock-api-token-m'
  }
  : {
    idToken: '',
    refreshToken: '',
    apiToken: '',
    apiTokenM: '',
    issuedAt: 0,
    expiresAt: 0,
    lastValidated: 0
  };

// Create the user store handle
const {
  store: userDataModel,
  itemStore: user,
  setItem: setUser,
  resetItem: resetUser,
  loadStoredItem: loadStoredUser,
  waitForCondition: _waitForUserCondition
} = createStoreHandle<User>(FILENAME, dummyUser);

const {
  store: userAuthModel,
  itemStore: userAuthStore,
  setItem: _setUserAuth,
  resetItem: resetUserAuth,
  loadStoredItem: loadStoredUserAuth,
  waitForCondition: _waitForAuthCondition
} = createStoreHandle<UserAuth>('com.cozyintellect.retain.keychain', dummyUserAuth);

const setUserAuth = async (
  userAuth: UserAuth,
  immediatePersist: boolean = false,
  status: StatusEnum = 'loaded',
  needsPatch: boolean = false
) => {
  if (!userAuth.idToken || !userAuth.refreshToken) {
    throw new Error('Setting user auth with no idToken or refreshToken not allowed');
  }
  await _setUserAuth(userAuth, immediatePersist, status, needsPatch);
};

// Export idiomatic methods
export {
  loadStoredUser,
  loadStoredUserAuth,
  resetUser,
  resetUserAuth,
  setUser,
  setUserAuth,
  user,
  userAuthModel,
  userAuthStore,
  userDataModel
};

export const userAuthToken = derived<
  Readable<UserAuth>,
  Pick<UserAuth, 'idToken' | 'refreshToken'>
>(userAuthStore, ($userAuth) => {
  const { idToken, refreshToken } = $userAuth;
  return {
    idToken,
    refreshToken
  };
});

export const userApiToken = derived<Readable<UserAuth>, string | null>(
  userAuthStore,
  ($userAuth) => {
    const { apiToken } = $userAuth;
    return apiToken;
  }
);

export const userApiTokenM = derived<Readable<UserAuth>, string | null>(
  userAuthStore,
  ($userAuth) => {
    const { apiTokenM } = $userAuth;
    return apiTokenM;
  }
);

export const isSignedIn = derived([user, userAuthToken], ([$user, $userAuthToken]) => {
  return !!($user?.id && $userAuthToken?.idToken && $userAuthToken?.refreshToken);
});

// Auth loading state for sign-in dialog
export const authLoading = writable<boolean>(false);

// Combined signed in or loading state
export const signedInOrLoading = derived([isSignedIn, authLoading], ([$isSignedIn, $authLoading]) => {
  return $isSignedIn || $authLoading;
});

export const onboardingData = derived(userDataModel, ($userDataModel): OnboardingData => {
  return {
    firstName: $userDataModel.item.firstName,
    lastName: $userDataModel.item.lastName,
    profile: $userDataModel.item.profile
  };
});

// Simulate an auth framework updating session variables dynamically
export const sessionStore = writable<Record<string, any>>({});

export const webSignedIn = derived([sessionStore, userAuthToken], ([$session, $userAuthToken]) => {
  const hasSession = Object.keys($session).length > 0;
  return !!$userAuthToken?.idToken && hasSession;
});
